/**
 * StatusFilter — Active / Not Active chips + Issues popover.
 *
 * - **Active / Not Active**: primary filter. Not Active = Inactive ∪ Terminated.
 *   Members still carry their underlying TeamMemberStatus so badges can show
 *   the exact lifecycle state; the filter is binary.
 * - **Issues ▾**: opens a popover listing the drift kinds (view-supplied —
 *   TEAM_DRIFT_KINDS or USERS_DRIFT_KINDS) with per-kind counts. Picking one
 *   constrains the dataset to drifted rows and overrides the Active/Not
 *   Active selection (drift cuts across status).
 */
import React from 'react';
import { Box, ChevronDownIcon, HStack, Popover, Pressable, VStack } from 'native-base';
import { Text } from '@pimm/base';
import type { DriftKind, StatusFilterValue } from '../types';

type Props = {
  value: StatusFilterValue;
  onChange: (next: StatusFilterValue) => void;
  counts: {
    active: number;
    notActive: number;
    drift: Partial<Record<DriftKind, number>>;
  };
  labels: {
    active: string;
    notActive: string;
    issues: string;
  };
  // The set of drift kinds shown in the Issues popover for this view —
  // Team view passes TEAM_DRIFT_KINDS, Users view passes USERS_DRIFT_KINDS.
  driftKinds: DriftKind[];
  // Per-kind label resolver — keeps the component independent of i18n keys.
  driftLabel: (kind: DriftKind) => string;
};

const Chip: React.FC<{
  label: string;
  count: number;
  isActive: boolean;
  onPress: () => void;
  trailing?: React.ReactNode;
}> = ({ label, count, isActive, onPress, trailing }) => (
  <Pressable onPress={onPress}>
    <HStack
      px={3}
      h={9}
      space={2}
      alignItems="center"
      justifyContent="center"
      rounded="lg"
      shadow={1}
      bg={isActive ? 'primary.50' : 'white'}
      borderWidth={1}
      borderColor={isActive ? 'primary.600' : 'gray.300'}
    >
      <Text size="sm" fontWeight={isActive ? 600 : 500} color={isActive ? 'primary.700' : 'gray.700'}>
        {label}
      </Text>
      <Text size="sm" fontWeight={600} color={isActive ? 'primary.700' : 'gray.500'}>
        {count}
      </Text>
      {trailing}
    </HStack>
  </Pressable>
);

export const StatusFilter: React.FC<Props> = ({ value, onChange, counts, labels, driftKinds, driftLabel }) => {
  const isActive = value === 'Active';
  const isNotActive = value === 'NotActive';
  const isDrift = typeof value === 'object' && value.kind === 'drift';
  const selectedDrift: DriftKind | null = isDrift ? (value as { kind: 'drift'; drift: DriftKind }).drift : null;

  const totalIssues = driftKinds.reduce((acc, k) => acc + (counts.drift[k] ?? 0), 0);
  const issuesChipLabel = selectedDrift ? `${labels.issues}: ${driftLabel(selectedDrift)}` : labels.issues;
  const issuesChipCount = selectedDrift ? counts.drift[selectedDrift] ?? 0 : totalIssues;

  return (
    <HStack space={2} alignItems="center">
      <Chip label={labels.active} count={counts.active} isActive={isActive} onPress={() => onChange('Active')} />
      <Chip label={labels.notActive} count={counts.notActive} isActive={isNotActive} onPress={() => onChange('NotActive')} />

      <Popover
        trigger={triggerProps => (
          <Pressable {...triggerProps}>
            <HStack
              px={3}
              h={9}
              space={2}
              alignItems="center"
              justifyContent="center"
              rounded="lg"
              shadow={1}
              bg={isDrift ? 'primary.50' : 'white'}
              borderWidth={1}
              borderColor={isDrift ? 'primary.600' : 'gray.300'}
            >
              <Text size="sm" fontWeight={isDrift ? 600 : 500} color={isDrift ? 'primary.700' : 'gray.700'}>
                {issuesChipLabel}
              </Text>
              <Text size="sm" fontWeight={600} color={isDrift ? 'primary.700' : 'gray.500'}>
                {issuesChipCount}
              </Text>
              <ChevronDownIcon size={3} color={isDrift ? 'primary.700' : 'gray.500'} />
            </HStack>
          </Pressable>
        )}
        placement="bottom left"
      >
        <Popover.Content w={280} borderWidth={0} shadow="7">
          <Popover.Body p={0} bg="white">
            <VStack py={1}>
              {driftKinds.map(kind => {
                const count = counts.drift[kind] ?? 0;
                const isSelected = selectedDrift === kind;
                return (
                  <Pressable
                    key={kind}
                    onPress={() => onChange({ kind: 'drift', drift: kind })}
                    _hover={{ bg: 'gray.50' }}
                    bg={isSelected ? 'primary.50' : undefined}
                  >
                    <HStack px={4} minH={9} alignItems="center" space={2} justifyContent="space-between">
                      <Text size="md" fontWeight={600} color={isSelected ? 'primary.700' : 'gray.800'} flex={1}>
                        {driftLabel(kind)}
                      </Text>
                      <Box bg={count > 0 ? 'amber.100' : 'gray.100'} px={2} py={0.5} rounded="md">
                        <Text size="xs" fontWeight={600} color={count > 0 ? 'amber.800' : 'gray.500'}>
                          {count}
                        </Text>
                      </Box>
                    </HStack>
                  </Pressable>
                );
              })}
            </VStack>
          </Popover.Body>
        </Popover.Content>
      </Popover>
    </HStack>
  );
};
